/**
 * Utilities for parsing HTML slides to/from Slide objects
 */ import { escapeHtml } from '../../../utils/htmlUtils';
/**
 * Determine slide type based on HTML structure
 */ function detectSlideType(section) {
    const hasH1 = section.querySelector('h1') !== null;
    const hasH2SectionHeader = section.querySelector('h2.section-header') !== null;
    const hasBullets = section.querySelector('ul') !== null;
    const isClosingSlide = section.classList.contains('closing-slide');
    if (isClosingSlide) return 'closing';
    if (hasH1) return 'title';
    if (hasH2SectionHeader) return 'section-header';
    if (hasBullets) return 'content-bullets';
    return 'content-text';
}
/**
 * Extract tables from a section element
 */ function extractTables(section) {
    const tables = section.querySelectorAll('table');
    if (tables.length === 0) return [];
    return Array.from(tables).map((table)=>{
        // Extract headers from thead or first row
        const headerRow = table.querySelector('thead tr') || table.querySelector('tr');
        const headers = [];
        if (headerRow) {
            const headerCells = headerRow.querySelectorAll('th, td');
            headerCells.forEach((cell)=>{
                headers.push(cell.textContent?.trim() || '');
            });
        }
        // Extract body rows
        const rows = [];
        const bodyRows = table.querySelectorAll('tbody tr');
        // If no tbody, get all rows except the first (header)
        const rowElements = bodyRows.length > 0 ? bodyRows : table.querySelectorAll('tr:not(:first-child)');
        rowElements.forEach((row)=>{
            const cells = [];
            row.querySelectorAll('td').forEach((cell)=>{
                // Get text content, handling nested elements like badges
                cells.push(cell.textContent?.trim() || '');
            });
            if (cells.length > 0) {
                rows.push(cells);
            }
        });
        return {
            headers,
            rows
        };
    });
}
/**
 * Parse HTML content to extract individual slides as structured objects
 */ export function parseHTMLToSlides(htmlContent) {
    if (!htmlContent || !htmlContent.trim()) {
        return [];
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const sections = doc.querySelectorAll('section');
    return Array.from(sections).map((section, index)=>{
        const slideType = detectSlideType(section);
        // Extract title - check h1, h2, h3, or any heading, then fall back to first strong/b or first text
        const h1 = section.querySelector('h1');
        const h2 = section.querySelector('h2');
        const h3 = section.querySelector('h3');
        const anyHeading = section.querySelector('h1, h2, h3, h4, h5, h6');
        const strongOrBold = section.querySelector('strong, b');
        // Try various title sources in order of preference
        let title = (h1 || h2 || h3 || anyHeading)?.textContent?.trim() || '';
        // If no heading found, try strong/bold text as title
        if (!title && strongOrBold) {
            title = strongOrBold.textContent?.trim() || '';
        }
        // If still no title, use first non-empty text content (truncated)
        if (!title) {
            const textContent = section.textContent?.trim() || '';
            if (textContent) {
                // Take first 50 chars as title
                title = textContent.substring(0, 50) + (textContent.length > 50 ? '...' : '');
            }
        }
        // Extract subtitle (for title slides)
        const subtitleEl = section.querySelector('p.subtitle, .subtitle');
        const subtitle = subtitleEl?.textContent?.trim();
        // Extract bullets
        const ul = section.querySelector('ul');
        const bullets = ul ? Array.from(ul.querySelectorAll(':scope > li')).map((li)=>({
                point: li.textContent?.trim() || ''
            })) : undefined;
        // Extract paragraphs (non-subtitle)
        const paragraphs = Array.from(section.querySelectorAll('p:not(.subtitle)')).map((p)=>p.textContent?.trim() || '').filter((text)=>text.length > 0);
        // Extract tables
        const tables = extractTables(section);
        // Build content object
        const hasContent = bullets || paragraphs.length > 0 || tables.length > 0;
        const content = hasContent ? {
            bullets,
            paragraphs: paragraphs.length > 0 ? paragraphs : undefined,
            tables: tables.length > 0 ? tables : undefined
        } : undefined;
        return {
            id: `slide-${index}-${Date.now()}`,
            type: slideType,
            title,
            subtitle,
            content
        };
    });
}
/**
 * Serialize slides back to HTML format for storage
 */ export function serializeSlidesToHTML(slides) {
    if (!slides || slides.length === 0) {
        return '';
    }
    return slides.map((slide)=>{
        let html = '<section>\n';
        if (slide.type === 'title') {
            // Title slide
            html += `  <h1>${escapeHtml(slide.title)}</h1>\n`;
            if (slide.subtitle) {
                html += `  <p class="subtitle">${escapeHtml(slide.subtitle)}</p>\n`;
            }
        } else if (slide.type === 'section-header') {
            // Section header slide
            html += `  <h2 class="section-header">${escapeHtml(slide.title)}</h2>\n`;
            if (slide.subtitle) {
                html += `  <p class="section-subtitle">${escapeHtml(slide.subtitle)}</p>\n`;
            }
        } else {
            // Content slides (bullets or text)
            html += `  <h2>${escapeHtml(slide.title)}</h2>\n`;
            // Add paragraphs
            if (slide.content?.paragraphs && slide.content.paragraphs.length > 0) {
                for (const paragraph of slide.content.paragraphs){
                    if (paragraph.trim()) {
                        html += `  <p>${escapeHtml(paragraph)}</p>\n`;
                    }
                }
            }
            // Add bullets (flat structure, no sub-bullets for now)
            if (slide.content?.bullets && slide.content.bullets.length > 0) {
                html += '  <ul>\n';
                for (const bullet of slide.content.bullets){
                    if (bullet.point.trim()) {
                        html += `    <li>${escapeHtml(bullet.point)}</li>\n`;
                    }
                }
                html += '  </ul>\n';
            }
        }
        html += '</section>';
        return html;
    }).join('\n\n');
}
